import * as THREE from 'three';

/**
 * WASD + mouse fly camera. Click the canvas to capture the mouse (pointer lock).
 * W/S forward/back, A/D strafe, E/Space up, Q/C down, Shift = 4x, wheel or +/- adjusts base speed.
 */
export class FlyCamera {
  speed = 40; // metres per second
  readonly minSpeed = 1;
  readonly maxSpeed = 3000;
  yaw = 0;
  pitch = -0.25;
  /** Keys held (KeyboardEvent.code); the walk and drive modes read them too. */
  readonly keys = new Set<string>();
  private readonly dom: HTMLElement;
  /** Off while walking or driving: the mouse still turns the view, the keys don't fly. */
  enabled = true;
  /** Mouse look: radians per pixel, and whether up and down are swapped. Kept in localStorage. */
  sensitivity = 0.0022;
  invertY = false;

  constructor(
    readonly camera: THREE.PerspectiveCamera,
    dom: HTMLElement,
  ) {
    this.dom = dom;
    try {
      const m = JSON.parse(localStorage.getItem(MOUSE_STORE) ?? '{}') as { sensitivity?: number; invertY?: boolean };
      if (typeof m.sensitivity === 'number') this.sensitivity = Math.max(0.0005, Math.min(0.008, m.sensitivity));
      if (typeof m.invertY === 'boolean') this.invertY = m.invertY;
    } catch {
      /* defaults */
    }
    dom.addEventListener('click', () => {
      if (document.pointerLockElement !== dom) void dom.requestPointerLock();
    });
    document.addEventListener('mousemove', (e) => {
      if (document.pointerLockElement !== dom) return;
      this.yaw -= e.movementX * this.sensitivity;
      this.pitch -= e.movementY * this.sensitivity * (this.invertY ? -1 : 1);
      this.pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, this.pitch));
    });
    window.addEventListener('keydown', (e) => {
      if (isTyping(e)) return;
      this.keys.add(e.code);
      if (!this.enabled) return;
      if (e.code === 'Equal' || e.code === 'NumpadAdd') this.adjustSpeed(1.25);
      if (e.code === 'Minus' || e.code === 'NumpadSubtract') this.adjustSpeed(0.8);
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => this.keys.clear());
    dom.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        if (this.enabled) this.adjustSpeed(e.deltaY < 0 ? 1.15 : 1 / 1.15);
      },
      { passive: false },
    );
  }

  setMouse(sensitivity: number, invertY: boolean): void {
    this.sensitivity = sensitivity;
    this.invertY = invertY;
    try {
      localStorage.setItem(MOUSE_STORE, JSON.stringify({ sensitivity, invertY }));
    } catch {
      /* not saved */
    }
  }

  adjustSpeed(f: number): void {
    this.speed = Math.max(this.minSpeed, Math.min(this.maxSpeed, this.speed * f));
  }

  lookAt(target: THREE.Vector3): void {
    const d = target.clone().sub(this.camera.position).normalize();
    this.pitch = Math.asin(Math.max(-1, Math.min(1, d.y)));
    this.yaw = Math.atan2(-d.x, -d.z);
  }

  get heading(): number {
    return this.yaw;
  }

  get isLocked(): boolean {
    return document.pointerLockElement === this.dom;
  }

  update(dt: number): void {
    const cam = this.camera;
    cam.quaternion.setFromEuler(new THREE.Euler(this.pitch, this.yaw, 0, 'YXZ'));
    if (!this.enabled) return;
    const move = new THREE.Vector3();
    if (this.keys.has('KeyW')) move.z -= 1;
    if (this.keys.has('KeyS')) move.z += 1;
    if (this.keys.has('KeyA')) move.x -= 1;
    if (this.keys.has('KeyD')) move.x += 1;
    let up = 0;
    if (this.keys.has('KeyE') || this.keys.has('Space')) up += 1;
    if (this.keys.has('KeyQ') || this.keys.has('KeyC')) up -= 1;
    const boost = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight') ? 4 : 1;
    if (move.lengthSq() > 0) {
      move.normalize().applyQuaternion(cam.quaternion);
      cam.position.addScaledVector(move, this.speed * boost * dt);
    }
    cam.position.y += up * this.speed * boost * dt;
  }
}

function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
}

const MOUSE_STORE = 'perpcity.mouse';
